/** @jsxImportSource @emotion/react */
import React, {
  useCallback,
  useEffect,
  useState,
  Suspense,
  useMemo,
} from 'react';
import { jsx, css } from '@emotion/react';
import { FormattedMessage, useIntl, defineMessage } from 'components/i18n';
import PageHeader from '@atlaskit/page-header';
import CustomerSelect from './components/CustomerSelect';
import TextField from 'components/textfield';
import Button, { ButtonGroup } from '@atlaskit/button';
import Spinner from '@atlaskit/spinner';

import { usePaginatedQuery, queryCache } from 'react-query';
import { useLocation, Link } from 'react-router-dom';
import { DateFilter, Sale } from './types';
import api from '../../libs/api';
import SalesTable from './components/SalesTable';
import { DatePicker } from '../../components/date-picker';
import TablePage from '../../components/page/TablePage';
import StatusFilter from './components/StatusFilter';
import SearchBar from '../../components/SearchBar';
import useApiQuery from '../../hooks/useApiQuery';

const tabs = [
  { label: 'All', content: <span /> },
  { label: 'Outstanding', content: <span /> },
  { label: 'Concept', content: <span /> },
  { label: 'Paid', content: <span /> },
];

export const fetchInvoices =  async (key: string, filter: any ) => {
  const {page, pageSize, sortBy, search, dateFrom, dateTo, status} = filter;
    if (!page || !pageSize) return null;
    const invoiceDate: DateFilter = {
      lte: null,
      gte: null,
    };
    if (dateFrom) {
      invoiceDate.gte = new Date(dateFrom);
    }
    if (dateTo) {
      invoiceDate.lte = new Date(dateTo);
    }
    const data = await api.get(`/${key}/search`, {
      params: {
        query: search,
        invoiceDate,
        status,
        limit: pageSize,
        order: sortBy,
        page,
      },
    });
    return data;
  };

const SalesPage = () => {
  const intl = useIntl();

  const { queryParams, debouncedUpdateQueryParams, updateQueryParams, handleChangeQueryParam } = useApiQuery();
  const { page, sortBy } = queryParams;
  const {search: searchQ, dateTo, dateFrom, status} = queryParams;
  const [search, setSearch] = useState<string>(searchQ);

  const {
    latestData,
    resolvedData,
    isFetching,
    isLoading,
  } = usePaginatedQuery(
    ['sales', queryParams],
    fetchInvoices
  );

  // Prefetch the next page!
  React.useEffect(() => {
    if (page) {
      queryCache.prefetchQuery(
        ['sales', {...queryParams, page: queryParams.page + 1}],
        fetchInvoices
      );
    }
  }, [resolvedData]);

  const handleSearch = React.useCallback(
    (value) => {
      setSearch(value)
      debouncedUpdateQueryParams({ search: value, page: 1});
    },
    [debouncedUpdateQueryParams]
  );

  const barContent = React.useMemo(() => {
    const searchLabel = intl.formatMessage({
      id: 'sale.SalesPage.search',
      defaultMessage: 'Search',
      description: 'Search placeholder',
    });
    const customerSelectLabel = intl.formatMessage({
      id: 'sale.SalesPage.customerSelectPlaceholder',
      defaultMessage: 'All customers',
      description: 'Placeholder for the customer select filter',
    });

    const dateFromPlaceholder = intl.formatMessage({
      id: 'sale.SalesPage.dateFromPlaceholder',
      defaultMessage: 'From',
    });
    const dateToPlaceholder = intl.formatMessage({
      id: 'sale.SalesPage.dateToPlaceholder',
      defaultMessage: 'To',
    });

    const itemsContainer = css`
      display: flex;
      align-items: center;
      margin-top: 1rem;
      margin-bottom: 1rem;
      && > div:not(:first-of-type) {
        margin-left: 8px;
      }
    `;

    return (
      <div>
        <div css={itemsContainer}>
          <div css={css`flex: 0 0 300px;`}>
            <SearchBar
              placeholder={searchLabel}
              value={search}
              onChange={handleSearch}
              onReset={() => handleSearch('')}
            />
          </div>
          {/*<div style={{ flex: '0 0 240px', marginLeft: 8, zIndex: 2 }}>*/}
          {/*  <CustomerSelect*/}
          {/*    placeholder={customerSelectLabel}*/}
          {/*    aria-label={customerSelectLabel}*/}
          {/*    spacing="compact"*/}
          {/*  />*/}
          {/*</div>*/}
          <div css={css`flex: 0 0 100px;`}>
            <StatusFilter value={status} onChange={handleChangeQueryParam('status')} />
          </div>
          <div css={css`flex: 0 0 135px;`}>
            <DatePicker
              defaultValue={dateFrom!}
              onChange={handleChangeQueryParam('dateFrom')}
              placeholder={dateFromPlaceholder}
              spacing="compact"
            />
          </div>
          <div css={css`flex: 0 0 135px;`}>
            <DatePicker
              defaultValue={dateTo!}
              onChange={handleChangeQueryParam('dateTo')}
              placeholder={dateToPlaceholder}
              spacing="compact"
            />
          </div>
          <div>{isFetching && <Spinner/>}</div>
          </div>
      </div>
    );
  }, [intl, status, isFetching, search]);
  console.log('isFetching',isFetching);
  const actionsContent = React.useMemo(
    () => (
      <ButtonGroup>
        <Link to="new">
          <Button appearance="primary" onClick={() => null}>
            <FormattedMessage
              id="sale.SalesPage.create"
              defaultMessage="Create invoice"
            />
          </Button>
        </Link>
        {/*<Button*/}
        {/*  // appearance="subtle"*/}
        {/*  onClick={() => null}*/}
        {/*>*/}
        {/*  <FormattedMessage id="sale.SalesPage.export" defaultMessage="Export" />*/}
        {/*</Button>*/}
      </ButtonGroup>
    ),
    []
  );

  const header = (
    <PageHeader bottomBar={barContent} actions={actionsContent}>
      <FormattedMessage id="sale.SalesPage.title" defaultMessage="Sales" />
    </PageHeader>
  );

  return (
    <TablePage header={header}>
      <Suspense fallback={<div>Loading SalesTable ...</div>}>
        <SalesTable
          data={latestData?.data}
          pageCount={latestData?.meta?.totalPageCount}
          updateQueryParams={updateQueryParams}
          isLoading={isLoading}
          {...queryParams}
        />
      </Suspense>
    </TablePage>
  );
};

export default SalesPage;
